import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  LayoutGrid, Bike, Wrench, Package, Users, LogOut, ScanLine, Menu, X, AlertTriangle, Contact, Layers, Tag, CalendarCheck, CalendarRange, ArrowDownUp, Sun, Moon, Monitor, Mail, MapPin, Megaphone, CreditCard, BarChart3
} from "lucide-react";
import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const ROLE_LABELS = {
  superadmin: "Super Admin", admin: "Admin", segreteria: "Segreteria", manutentore: "Manutentore",
};

function ThemeSwitch() {
  const { theme, resolved, setTheme, toggle } = useTheme();
  const opts = [
    { v: "light", icon: Sun, label: "Chiaro" },
    { v: "dark", icon: Moon, label: "Scuro" },
    { v: "system", icon: Monitor, label: "Auto" },
  ];
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between px-2 mb-1.5">
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Tema</span>
        <button onClick={toggle} data-testid="theme-quick-toggle" title="Inverti tema"
          className="text-zinc-400 hover:text-[#f98125] transition-colors">
          {resolved === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
      </div>
      <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-sm p-1">
        {opts.map((o) => (
          <button key={o.v} onClick={() => setTheme(o.v)} data-testid={`theme-${o.v}`} title={o.label}
            className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-sm text-[11px] font-medium transition-colors ${theme === o.v ? "bg-[#f98125] text-white" : "text-zinc-400 hover:text-white"}`}>
            <o.icon className="h-3.5 w-3.5" /> {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function Layout({ children }) {
  const { user, logout, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const nav = [
    { to: "/", label: "Dashboard", icon: LayoutGrid, show: true, end: true },
    { to: "/inventario", label: "Inventario", icon: Bike, show: hasPerm("inventory.view") },
    { to: "/categorie", label: "Categorie", icon: Layers, show: hasPerm("categories.view") },
    { to: "/listini", label: "Listini", icon: Tag, show: hasPerm("pricing.view") },
    { to: "/noleggi", label: "Noleggi", icon: CalendarCheck, show: hasPerm("rentals.view") },
    { to: "/calendario", label: "Calendario", icon: CalendarRange, show: hasPerm("rentals.view") },
    { to: "/manutenzione", label: "Manutenzione", icon: Wrench, show: hasPerm("maintenance.view") },
    { to: "/magazzino", label: "Magazzino", icon: Package, show: hasPerm("warehouse.view") },
    { to: "/segnalazioni", label: "Segnalazioni", icon: AlertTriangle, show: hasPerm("reports.view") },
    { to: "/clienti", label: "Clienti", icon: Contact, show: hasPerm("customers.view") },
    { to: "/amministrazione", label: "Amministrazione", icon: Users, show: hasPerm("users.manage") },
    { to: "/sedi", label: "Sedi", icon: MapPin, show: hasPerm("locations.manage") },
    { to: "/import-export", label: "Import/Export", icon: ArrowDownUp, show: hasPerm("import_export.view") },
    { to: "/template-email", label: "Template Email", icon: Mail, show: hasPerm("email_templates.view") },
    { to: "/marketing", label: "Marketing", icon: Megaphone, show: hasPerm("marketing.manage") },
    { to: "/pagamenti", label: "Pagamenti", icon: CreditCard, show: hasPerm("payments.manage") },
    { to: "/statistiche", label: "Statistiche", icon: BarChart3, show: hasPerm("statistics.view") },
  ].filter((n) => n.show);

  return (
    <div className="min-h-screen flex bg-[#0A0A0A]">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 h-full w-64 bg-[#0d0d0d] border-r border-zinc-800 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="px-6 py-6 border-b border-zinc-800 flex items-center gap-3">
          <div className="h-9 w-9 bg-[#f98125] rounded-sm flex items-center justify-center">
            <Bike className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-display text-2xl leading-none tracking-tight">BICIMON-E</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Officina · Flotta</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                  isActive ? "bg-[#f98125] text-white" : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
                }`
              }>
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-zinc-800">
          <ThemeSwitch />
          <div className="flex items-center gap-3 px-2 mb-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-zinc-800 text-xs">{user?.name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="text-sm truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#f98125]">{ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
          </div>
          <button onClick={logout} data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm text-zinc-400 hover:bg-zinc-900 hover:text-white transition-colors">
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-[#0d0d0d]">
          <button onClick={() => setOpen(true)} data-testid="menu-toggle"><Menu className="h-6 w-6" /></button>
          <div className="font-display text-xl">E-FLEET</div>
          <button onClick={() => navigate("/scanner")} data-testid="mobile-scanner-btn"><ScanLine className="h-6 w-6 text-[#f98125]" /></button>
        </header>
        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
